import React from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { ArrowLeft, FileText, AlertTriangle, Scale, Users } from "lucide-react";
import { APP_CONFIG, CONTACT_INFO, LEGAL_INFO } from "@/utils/constants/app";

interface TermsOfServicePageProps {
  onBackToHome: () => void;
}

export function TermsOfServicePage({ onBackToHome }: TermsOfServicePageProps) {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-blue-600 text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center mb-6">
            <Button
              variant="ghost"
              onClick={onBackToHome}
              className="text-white hover:bg-blue-700 mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Torna alla Home
            </Button>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Scale className="w-12 h-12 mr-4" />
              <div>
                <h1 className="text-4xl lg:text-5xl font-bold mb-2">
                  Termini di Servizio
                </h1>
                <p className="text-xl opacity-90">
                  Condizioni Generali di Utilizzo del Sito Web
                </p>
              </div>
            </div>
            <p className="text-lg opacity-80 max-w-3xl mx-auto">
              Questi termini disciplinano l&apos;utilizzo del sito web e dei servizi offerti da {APP_CONFIG.COMPANY_NAME}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          
          {/* Ultima modifica */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <p className="text-sm text-blue-800">
              <strong>Ultimo aggiornamento:</strong> 12 Agosto 2025
            </p>
          </div>

          <div className="space-y-8">
            
            {/* Premessa */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-2xl">
                  <FileText className="w-6 h-6 mr-3 text-blue-600" />
                  1. Premessa e Accettazione
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  L&apos;utilizzo del presente sito web comporta l&apos;accettazione integrale dei seguenti termini e condizioni. 
                  Se non accetti questi termini, ti invitiamo a non utilizzare il sito.
                </p>
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                  <p className="text-blue-800">
                    <strong>Soggetto:</strong> {APP_CONFIG.COMPANY_NAME}<br/>
                    <strong>P.IVA:</strong> {LEGAL_INFO.VAT_NUMBER}<br/>
                    <strong>Sede:</strong> {CONTACT_INFO.FULL_ADDRESS}<br/>
                    <strong>Email:</strong> {CONTACT_INFO.PRIMARY_EMAIL}<br/>
                    <strong>Telefono:</strong> {CONTACT_INFO.MAIN_PHONE}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Definizioni */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-2xl">
                  <Users className="w-6 h-6 mr-3 text-blue-600" />
                  2. Definizioni
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="border-l-4 border-green-500 pl-4">
                    <p><strong>&quot;Sito&quot;:</strong> il presente sito web {APP_CONFIG.DOMAIN}</p>
                  </div>
                  <div className="border-l-4 border-blue-500 pl-4">
                    <p><strong>&quot;Società&quot;:</strong> {APP_CONFIG.COMPANY_NAME}</p>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4">
                    <p><strong>&quot;Utente&quot;:</strong> qualsiasi persona fisica che accede e utilizza il Sito</p>
                  </div>
                  <div className="border-l-4 border-orange-500 pl-4">
                    <p><strong>&quot;Servizi&quot;:</strong> tutti i servizi di disinfestazione, derattizzazione e ambientali offerti dalla Società</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Utilizzo del Sito */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">3. Utilizzo del Sito</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <h4 className="font-semibold text-gray-900 mb-3">Uso Consentito</h4>
                <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
                  <li>Consultazione delle informazioni sui servizi offerti</li>
                  <li>Richiesta di preventivi e informazioni</li>
                  <li>Contatto con la Società tramite i moduli disponibili</li>
                  <li>Navigazione nel rispetto delle presenti condizioni</li>
                </ul>

                <h4 className="font-semibold text-gray-900 mb-3 text-red-600">Uso Vietato</h4>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <ul className="list-disc list-inside text-red-700 space-y-1 text-sm">
                    <li>Utilizzo per scopi illegali o non autorizzati</li>
                    <li>Tentativo di accesso non autorizzato ai sistemi</li>
                    <li>Invio di virus, malware o codice dannoso</li>
                    <li>Copia, riproduzione o distribuzione non autorizzata di contenuti</li>
                    <li>Spam o comunicazioni commerciali non richieste</li>
                    <li>Violazione dei diritti di proprietà intellettuale</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Servizi Offerti */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">4. Servizi e Preventivi</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Servizi Principali</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-1 text-sm">
                      <li>Disinfestazione (insetti striscianti, volanti, aracnidi)</li>
                      <li>Derattizzazione (topi, ratti)</li>
                      <li>Sanificazione ambientale</li>
                      <li>Allontanamento volatili</li>
                      <li>Diserbo</li>
                      <li>Pest Management</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Condizioni Preventivi</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-1 text-sm">
                      <li>Preventivi gratuiti e senza impegno</li>
                      <li>Validità 30 giorni dalla data di emissione</li>
                      <li>Sopralluogo necessario per preventivi definitivi</li>
                      <li>Prezzi IVA inclusa salvo diversa indicazione</li>
                      <li>Possibili variazioni in base alla situazione reale</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                  <p className="text-yellow-800">
                    <strong>Importante:</strong> I preventivi online sono indicativi. Il prezzo definitivo verrà confermato 
                    dopo sopralluogo tecnico gratuito.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Proprietà Intellettuale */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">5. Proprietà Intellettuale</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  Tutti i contenuti del Sito, inclusi ma non limitati a testi, immagini, loghi, grafica, 
                  software e design, sono di proprietà della Società o utilizzati su licenza.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-green-800 mb-2">Diritti Riservati</h4>
                    <ul className="list-disc list-inside text-green-700 space-y-1 text-sm">
                      <li>Marchi e loghi</li>
                      <li>Contenuti testuali</li>
                      <li>Immagini e grafiche</li>
                      <li>Codice e software</li>
                    </ul>
                  </div>
                  
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-800 mb-2">Uso Consentito</h4>
                    <ul className="list-disc list-inside text-blue-700 space-y-1 text-sm">
                      <li>Visualizzazione personale</li>
                      <li>Stampa per uso privato</li>
                      <li>Condivisione sui social (con attribuzione)</li>
                      <li>Citazione con fonte</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Limitazione di Responsabilità */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center text-2xl">
                  <AlertTriangle className="w-6 h-6 mr-3 text-orange-600" />
                  6. Limitazione di Responsabilità
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-orange-800 leading-relaxed">
                    <strong>Disclaimer:</strong> La Società si impegna a mantenere il Sito aggiornato e funzionante, 
                    tuttavia non può garantire l&apos;assenza di errori o interruzioni del servizio.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">La Società non è responsabile per:</h4>
                    <ul className="list-disc list-inside text-gray-700 space-y-1">
                      <li>Interruzioni temporanee del servizio</li>
                      <li>Errori o omissioni nei contenuti</li>
                      <li>Danni derivanti dall&apos;uso improprio del Sito</li>
                      <li>Perdita di dati o informazioni</li>
                      <li>Contenuti di siti esterni collegati</li>
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Limitazioni:</h4>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      In ogni caso, la responsabilità massima della Società è limitata al valore del servizio 
                      acquistato. Sono esclusi danni indiretti, consequenziali o punitivi.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Privacy e Cookie */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">7. Privacy e Cookie</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-700 leading-relaxed">
                  Il trattamento dei dati personali e l&apos;utilizzo dei cookie sono disciplinati dalle rispettive policy:
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-800 mb-2">Privacy Policy</h4>
                    <p className="text-purple-700 text-sm">
                      Consulta la nostra Privacy Policy per informazioni dettagliate su come trattiamo i tuoi dati personali.
                    </p>
                  </div>
                  
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <h4 className="font-semibold text-indigo-800 mb-2">Cookie Policy</h4>
                    <p className="text-indigo-700 text-sm">
                      La Cookie Policy descrive i cookie utilizzati e le tue opzioni di controllo.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Modifiche e Legge Applicabile */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">8. Modifiche e Legge Applicabile</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Modifiche ai Termini</h4>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      La Società si riserva il diritto di modificare questi termini in qualsiasi momento. 
                      Le modifiche saranno pubblicate su questa pagina con indicazione della data di aggiornamento.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Legge Applicabile</h4>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Questi termini sono regolati dalla legge italiana. Per qualsiasi controversia 
                      è competente il Foro di Roma.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contatti */}
            <Card className="border-0 shadow-lg bg-blue-50">
              <CardHeader>
                <CardTitle className="text-2xl text-blue-800">9. Contatti</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-blue-700 leading-relaxed mb-4">
                  Per qualsiasi domanda relativa a questi Termini di Servizio, contattaci:
                </p>
                <div className="bg-white p-4 rounded-lg border border-blue-200">
                  <p><strong>Email:</strong> {CONTACT_INFO.PRIMARY_EMAIL}</p>
                  <p><strong>Telefono:</strong> {CONTACT_INFO.MAIN_PHONE}</p>
                  <p><strong>Indirizzo:</strong> {CONTACT_INFO.FULL_ADDRESS}</p>
                  <p><strong>Orari:</strong> Lunedì-Venerdì 8:00-18:00, Sabato 8:00-13:00</p>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>
    </div>
  );
}